import React from "react";
import Layout from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/section-header";
import { Helmet } from "react-helmet";

export default function ExpertMarketplaceTermsPage() {
  return (
    <Layout>
      <Helmet>
        <title>Expert Marketplace Terms | Grandpal</title>
        <meta name="description" content="Terms and conditions for experts joining Grandpal's care placement marketplace, including commission structure and service agreements." />
      </Helmet>
      
      <div className="container max-w-4xl py-16">
        <SectionHeader 
          title="Expert Marketplace Terms & Conditions"
          subtitle="Last updated: January 2025"
          centered
        />
        
        <div className="prose prose-lg max-w-none">
          <h3>1. Introduction</h3>
          <p>
            These Expert Marketplace Terms and Conditions ("Terms") govern your participation as an expert consultant in Grandpal's care placement marketplace. By submitting an application and agreeing to these terms, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </p>
          
          <h3>2. Commission Structure</h3>
          <p>
            <strong>Platform Fee:</strong> Grandpal charges a 20% commission on all consultation bookings facilitated through our platform.
          </p>
          <ul>
            <li><strong>Expert Payment:</strong> You will receive 80% of your consultation fee for each completed booking</li>
            <li><strong>Platform Services:</strong> The 20% fee covers client matching, secure payment processing, platform maintenance, customer support, and marketing services</li>
            <li><strong>Payment Timing:</strong> Payments are processed within 3-5 business days after consultation completion</li>
            <li><strong>Tax Responsibility:</strong> You are responsible for reporting and paying taxes on your earnings</li>
          </ul>

          <h3>3. Services Provided by Grandpal</h3>
          <p>In exchange for the 20% platform fee, Grandpal provides:</p>
          <ul>
            <li><strong>Client Matching:</strong> Connecting you with families who need your specific expertise</li>
            <li><strong>Secure Payment Processing:</strong> Handling all payment transactions and invoicing</li>
            <li><strong>Booking Management:</strong> Calendar integration and appointment scheduling</li>
            <li><strong>Communication Tools:</strong> Messaging and video call integration</li>
            <li><strong>Professional Profile:</strong> Showcasing your expertise to potential clients</li>
            <li><strong>Customer Support:</strong> Handling client inquiries and technical issues</li>
            <li><strong>Marketing Support:</strong> Promoting the marketplace and driving client traffic</li>
            <li><strong>Platform Maintenance:</strong> Ongoing development and security updates</li>
          </ul>

          <h3>4. Expert Responsibilities</h3>
          <p>As an expert consultant, you agree to:</p>
          <ul>
            <li>Provide accurate and truthful information in your application and profile</li>
            <li>Maintain professional standards and ethical conduct in all client interactions</li>
            <li>Deliver services as described in your profile and agreed upon with clients</li>
            <li>Respond to client inquiries and booking requests promptly</li>
            <li>Maintain appropriate professional certifications and qualifications</li>
            <li>Update your availability and pricing as needed</li>
            <li>Provide high-quality, professional consultation services</li>
            <li>Maintain client confidentiality and privacy</li>
          </ul>

          <h3>5. Application and Approval Process</h3>
          <ul>
            <li>All applications are manually reviewed by our team</li>
            <li>Review process typically takes 3-5 business days</li>
            <li>Approval is based on qualifications, experience, and fit with our marketplace</li>
            <li>We reserve the right to reject applications at our discretion</li>
            <li>Approved experts must complete profile setup before receiving bookings</li>
          </ul>

          <h3>6. Pricing and Payment</h3>
          <ul>
            <li>You set your own consultation rates and pricing</li>
            <li>Pricing must be competitive and reasonable for the market</li>
            <li>All payments are processed through our secure platform</li>
            <li>You receive 80% of your consultation fee after each completed booking</li>
            <li>Payment processing fees are included in the 20% platform fee</li>
            <li>You are responsible for all tax obligations related to your earnings</li>
          </ul>

          <h3>7. Client Relationships</h3>
          <ul>
            <li>Clients are matched with you based on expertise, availability, and pricing</li>
            <li>You are responsible for the quality and delivery of your consultation services</li>
            <li>Client feedback and reviews may be displayed on your profile</li>
            <li>You must maintain professional boundaries and ethical standards</li>
            <li>Any disputes with clients should be resolved professionally and promptly</li>
          </ul>

          <h3>8. Platform Usage</h3>
          <ul>
            <li>You agree to use the platform only for legitimate consultation services</li>
            <li>You will not attempt to circumvent the platform or arrange direct payments</li>
            <li>You will not use the platform for any illegal or unethical activities</li>
            <li>You will maintain the security of your account credentials</li>
            <li>You will report any technical issues or security concerns promptly</li>
          </ul>

          <h3>9. Termination</h3>
          <ul>
            <li>Either party may terminate this agreement with 30 days written notice</li>
            <li>We may suspend or terminate your account for violations of these terms</li>
            <li>Upon termination, you will receive payment for all completed consultations</li>
            <li>You agree to complete any scheduled consultations before termination</li>
            <li>We may remove your profile from the marketplace upon termination</li>
          </ul>

          <h3>10. Liability and Indemnification</h3>
          <ul>
            <li>You are responsible for the quality and accuracy of your consultation services</li>
            <li>You agree to indemnify Grandpal against any claims arising from your services</li>
            <li>Grandpal is not liable for any damages resulting from your consultation services</li>
            <li>You are responsible for maintaining appropriate professional liability insurance</li>
          </ul>

          <h3>11. Privacy and Data Protection</h3>
          <ul>
            <li>You must comply with all applicable data protection laws</li>
            <li>Client information shared with you must be kept confidential</li>
            <li>You agree to our Privacy Policy regarding data handling</li>
            <li>You will not share client information with third parties without consent</li>
          </ul>

          <h3>12. Changes to Terms</h3>
          <p>
            We reserve the right to modify these terms at any time. Changes will be communicated to you via email or through the platform. Continued use of the platform after changes constitutes acceptance of the new terms.
          </p>

          <h3>13. Contact Information</h3>
          <p>
            For questions about these terms or the expert marketplace, please contact us at:
          </p>
          <ul>
            <li>Email: experts@grandpal.ie</li>
            <li>Phone: +353 1 XXX XXXX</li>
            <li>Address: [Your Business Address]</li>
          </ul>

          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mt-8">
            <h4 className="text-blue-800 font-medium mb-2">Important Reminder</h4>
            <p className="text-blue-700 text-sm">
              By submitting your expert application, you acknowledge that you have read, understood, and agree to these Terms and Conditions. The 20% platform fee covers all the services we provide to help you succeed in the marketplace.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
} 